import type { ReactNode } from 'react';
import type { I18nKey } from '../i18n/context.tsx';
import { useT } from '../i18n/index.ts';

/**
 * Three-column feature grid. DESIGN.md §6.4.
 *
 * Each card:
 *   - 40×40 icon chip with `bg-claude-tint` and a ghost border, the
 *     icon glyph stroked in `text-claude-deep`.
 *   - 22px h3, then a body paragraph in `ink-muted`.
 *   - 5mm-tall meta row: a 6px olive dot + a mono label.
 *
 * The feature list is data-driven; add a tuple here plus the matching
 * `{prefix}.title` / `{prefix}.body` / `{prefix}.meta` keys in
 * `i18n/zh-CN.ts` + `i18n/en.ts`. Icon glyphs are inline SVG so we
 * don't pull in an icon font for three glyphs.
 */

interface Feature {
  readonly key: string;
  readonly titleKey: I18nKey;
  readonly bodyKey: I18nKey;
  readonly metaKey: I18nKey;
  readonly icon: ReactNode;
}

const BACKBONE_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    className="h-[19px] w-[19px]"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9.5" />
    <path d="M2.5 12h19M12 2.5c3.5 3 3.5 16 0 19M12 2.5c-3.5 3-3.5 16 0 19" />
  </svg>
);

const BILLING_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    className="h-[19px] w-[19px]"
    aria-hidden="true"
  >
    <path d="M3 7h18M3 12h18M3 17h12" />
    <circle cx="19" cy="17" r="2.2" />
  </svg>
);

const SDK_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    className="h-[19px] w-[19px]"
    aria-hidden="true"
  >
    <path d="M8 3L4 7l4 4M16 21l4-4-4-4M14.5 4L9.5 20" />
  </svg>
);

const FEATURES: readonly Feature[] = [
  {
    key: 'backbone',
    titleKey: 'features.backbone.title',
    bodyKey: 'features.backbone.body',
    metaKey: 'features.backbone.meta',
    icon: BACKBONE_ICON,
  },
  {
    key: 'billing',
    titleKey: 'features.billing.title',
    bodyKey: 'features.billing.body',
    metaKey: 'features.billing.meta',
    icon: BILLING_ICON,
  },
  {
    key: 'sdk',
    titleKey: 'features.sdk.title',
    bodyKey: 'features.sdk.body',
    metaKey: 'features.sdk.meta',
    icon: SDK_ICON,
  },
];

export function Features(): JSX.Element {
  const t = useT();
  return (
    <section id="features" className="py-28" data-testid="features">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-20 max-w-3xl">
          <p className="mb-4 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-claude">
            {t('features.eyebrow')}
          </p>
          <h2 className="text-[44px] font-semibold leading-[1.05] text-ink md:text-[56px]">
            {t('features.title.lead')}
            <span className="serif text-claude">{t('features.title.accent')}</span>
          </h2>
          <p className="mt-6 max-w-2xl text-[17px] leading-[1.65] text-ink-muted">
            {t('features.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-x-12 gap-y-16 md:grid-cols-3">
          {FEATURES.map((feature) => (
            <div key={feature.key} data-testid={`feature-${feature.key}`}>
              <div className="ghost mb-6 flex h-11 w-11 items-center justify-center rounded-full bg-claude-tint text-claude-deep">
                {feature.icon}
              </div>
              <h3 className="mb-3 text-[22px] font-semibold tracking-tight-ink text-ink">
                {t(feature.titleKey)}
              </h3>
              <p className="text-[15px] leading-[1.7] text-ink-muted">{t(feature.bodyKey)}</p>
              <div className="mt-5 flex items-center gap-2 font-mono text-[12px] text-ink-subtle">
                <span className="h-1.5 w-1.5 rounded-full bg-olive" />
                {t(feature.metaKey)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
